import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectSocket } from "../socket";
import { openAuthenticatedFile, isServerProxyDocUrl } from "../utils/auth";
import SideBar from "./SideBar";
import UserProfileMenu from "./UserProfileMenu";

// Add category display names mapping
const categoryDisplayNames = {
  'defects': 'Defects',
  'Product docs': 'Product Docs',
  'case notes': 'Case Notes'
};

// Add category color mapping
const getCategoryColor = (category) => {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower === 'defects') {
    return {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      iconColor: 'text-blue-600',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-200'
    };
  } else if (categoryLower === 'case notes') {
    return {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      iconColor: 'text-purple-600',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-200'
    };
  } else if (categoryLower === 'product docs') {
    return {
      bg: 'bg-yellow-100',
      text: 'text-yellow-600',
      iconColor: 'text-yellow-600',
      border: 'border-yellow-200',
      hover: 'hover:bg-yellow-200'
    };
  }
  
  // Default fallback
  return {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    iconColor: 'text-gray-600',
    border: 'border-gray-200',
    hover: 'hover:bg-gray-200'
  };
};

const getBackendOrigin = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  }

  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  if (isLocalhost) {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }

  return window.location.origin;
};

const withPdfPageHash = (link, pageNumber) => {
  if (!link || !pageNumber || !/\.pdf(?:[#?]|$)/i.test(link)) {
    return link;
  }

  const pageHash = `page=${pageNumber}`;
  if (/#page=\d+/i.test(link)) {
    return link.replace(/#page=\d+/i, `#${pageHash}`);
  }

  return `${link.split("#")[0]}#${pageHash}`;
};

const getCitationHost = (link = "") => {
  try {
    return new URL(link, window.location.origin).hostname;
  } catch {
    return "source";
  }
};

const CitationPreview = ({ source }) => (
  <div className="group relative inline-flex">
    <a
      href={source.link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${source.name}${source.pageNumber ? ` page ${source.pageNumber}` : ""}`}
      className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-gray-200 px-2 text-xs font-semibold text-gray-600 hover:bg-gray-300 transition"
    >
      {source.citationId}
    </a>
    <div className="pointer-events-none absolute bottom-8 left-0 z-50 hidden w-80 max-w-[calc(100vw-3rem)] rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl group-hover:block group-focus-within:block">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="truncate text-xs font-semibold text-gray-500">
          {source.category || "Product Documents"}
        </span>
        {source.pageNumber && (
          <span className="shrink-0 rounded bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">
            Page {source.pageNumber}
          </span>
        )}
      </div>
      <div className="mb-2 line-clamp-2 text-sm font-semibold leading-snug text-gray-900">
        {source.name || source.filename || "Source document"}
      </div>
      <p className="mb-3 line-clamp-3 text-xs leading-relaxed text-gray-600">
        {source.summary || "No summary available."}
      </p>
      <a
        href={source.link}
        target="_blank"
        rel="noopener noreferrer"
        className="pointer-events-auto inline-flex max-w-full items-center gap-2 truncate rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-semibold text-cyan-700 hover:border-cyan-200 hover:bg-cyan-50"
      >
        <span className="truncate">{getCitationHost(source.link)}</span>
        <span aria-hidden="true">Open</span>
      </a>
    </div>
  </div>
);

const normalizeSearchSource = (item, index) => {
  if (typeof item === "string") {
    const link = item.startsWith("/")
      ? `${getBackendOrigin()}${item}`
      : item;

    return {
      link,
      name: "Document",
      summary: "No Summary available.",
      category: "Product Documents",
      citationId: index + 1,
      icon: "pdf",
    };
  }

  const rawLink = item.link || "";
  const pageNumber = item.page_number || item.pageNumber;
  const link = rawLink.startsWith("/")
    ? `${getBackendOrigin()}${rawLink}`
    : rawLink || "#";

  return {
    link: withPdfPageHash(link, pageNumber),
    name: item.name || item.filename || "No File name found.",
    summary: item.summary || "No Summary available.",
    category: item.category || "Product Documents",
    citationId: item.citation_id || item.citationId || index + 1,
    pageNumber,
    filename: item.filename,
    icon: item.icon || "pdf",
    score: item.score ?? null,
  };
};

const DocLink = ({ link, isAuthGated, fileLabel, style, children, hoverUnderline }) =>
  isAuthGated ? (
    <span
      role="link"
      tabIndex={0}
      title={fileLabel || undefined}
      onClick={(e) => { e.preventDefault(); openAuthenticatedFile(link); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAuthenticatedFile(link); } }}
      style={{ cursor: 'pointer', ...style }}
      onMouseOver={hoverUnderline ? (e) => (e.currentTarget.style.textDecoration = 'underline') : undefined}
      onMouseOut={hoverUnderline ? (e) => (e.currentTarget.style.textDecoration = 'none') : undefined}
    >
      {children}
    </span>
  ) : (
    <a
      href={link}
      title={fileLabel || undefined}
      target="_blank"
      rel="noopener noreferrer"
      style={style}
      onMouseOver={hoverUnderline ? (e) => (e.currentTarget.style.textDecoration = 'underline') : undefined}
      onMouseOut={hoverUnderline ? (e) => (e.currentTarget.style.textDecoration = 'none') : undefined}
    >
      {children}
    </a>
  );

const TSEAgentRag = ({ onLogout, standalone = false, embedded = false, initialQuery = null, researchRequest = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  // When embedded (enterprise split view) we strip full-page chrome (fixed
  // title/menu/input) and auto-run initialQuery once the socket is ready.
  const autoRanRef = useRef(false);
  
  // Search mode states - matching ChatWindow structure
  const [searchMessages, setSearchMessages] = useState([]);
  const [searchLoadingStatus, setSearchLoadingStatus] = useState(null);
  const [searchSubMode, setSearchSubMode] = useState("rag");
  const [documentSummaries, setDocumentSummaries] = useState({});
  
  const [message, setMessage] = useState("");
  const [conversationSearchContext, setConversationSearchContext] = useState("");
  const [showSearchChoice, setShowSearchChoice] = useState(true);
  
  // Add support type state with a welcome message - matching ChatWindow
  const [supportType, setSupportType] = useState("WELCOME TO ARTA AI SUPPORT");
  const [displayTitle, setDisplayTitle] = useState("WELCOME TO ARTA AI SUPPORT");
  const [isTyping, setIsTyping] = useState(false);
  const [isTitleSet, setIsTitleSet] = useState(false);
  
  // Image modal state
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const handleImageClick = (url) => setZoomImageUrl(url);
  const closeZoom = () => setZoomImageUrl(null);
  
  // Add scroll indicator state - matching ChatWindow
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  
  const searchChatBodyRef = useRef(null);
  
  // Force light theme for TSE Dashboard
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Light theme configuration only - matching ChatWindow colors
  const theme = {
    light: {
      bg: 'bg-white',
      sidebarBg: 'bg-gray-50',
      cardBg: 'bg-white',
      headerBg: 'bg-gray-50',
      contentBg: 'bg-white',
      inputBg: 'bg-white',
      text: 'text-[#253439]',
      textSecondary: 'text-gray-600',
      textMuted: 'text-[#A2A9AF]',
      timestampColor: 'text-gray-400',
      border: 'border-[#06b6d4]',
      borderLight: 'border-gray-200',
      hover: 'hover:bg-gray-50',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-gray-50',
      accent: 'bg-[#06b6d4]',
      accentHover: 'hover:bg-cyan-700',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    }
  };
  
  const currentTheme = theme.light;
  
  // Typewriter effect hook - matching ChatWindow
  useEffect(() => {
    if (supportType === displayTitle) return;
    
    setIsTyping(true);
    let currentIndex = 0;
    const targetText = supportType;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= targetText.length) {
        setDisplayTitle(targetText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 50); // Adjust speed here (lower = faster)
    
    return () => clearInterval(typingInterval);
  }, [supportType]);
  
  // Add function to get title from message - matching ChatWindow
  const getTitleFromMessage = (message) => {
    const msg = message.toLowerCase();
    if (msg.includes("network")) return "NETWORK ISSUE";
    if (msg.includes("server")) return "SERVER ISSUE";
    if (msg.includes("graphiant")) return "GRAPHIANT ISSUE";
    return "WELCOME TO ARTA AI SUPPORT"; // Default title when no keywords found
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Request document summary function - matching ChatWindow
  const requestDocumentSummary = (documentPath, queryId = null) => {
    if (!documentPath) return;
    
    // Check if we already have a summary for this document
    if (documentSummaries[documentPath]) {
      return;
    }
    
    // Simulate AI summary generation
    setTimeout(() => {
      const mockSummary = `This document provides comprehensive information about ${documentPath.replace('.pdf', '')}. It includes detailed technical specifications, troubleshooting procedures, and best practices for implementation. The content is structured to help users understand complex concepts and apply them in real-world scenarios.`;
      
      setDocumentSummaries(prev => ({
        ...prev,
        [documentPath]: mockSummary
      }));
    }, 2000);
  };

  const formatTimestamp = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Format bot message function - matching ChatWindow
  const formatBotMessage = (text, isSearchResponse = false, sources = []) => {
    if (!text) return "";
    
    let formatted = text;
    
    // Convert markdown image syntax ![alt text](url) to modal-friendly links
    formatted = formatted.replace(
      /!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="javascript:void(0);" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$2" style="cursor:pointer;">$1</a>'
    );
    
    // Markdown headings → styled HTML
    formatted = formatted.replace(/^###\s+(.+)$/gm, '<h3 style="font-size:15px;font-weight:700;margin:16px 0 8px 0;color:#1e293b;">$1</h3>');
    formatted = formatted.replace(/^##\s+(.+)$/gm, '<h2 style="font-size:16px;font-weight:700;margin:16px 0 8px 0;color:#1e293b;">$1</h2>');

    // Bold text: **text** → <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Markdown links [text](url) → clickable anchor (before line break conversion).
    // S3-proxied citation links (/api/images/...) are auth-gated, so they must be
    // fetched with a Bearer token and opened as a blob rather than navigated to.
    formatted = formatted.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => {
      if (isServerProxyDocUrl(url)) return `<a href="javascript:void(0);" class="citation-link text-blue-600 hover:text-blue-800 underline" data-doc-url="${url}" style="cursor:pointer;">${label}</a>`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${label}</a>`;
    });

    // Bare URLs → clickable anchor fallback
    const urlRegex = /(?<!")\b(https?:\/\/(?:[^\s<>)"&]|&(?!lt;|gt;))+)(?!")/gi;
    formatted = formatted.replace(urlRegex, (match) => {
      return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });

    // Convert line breaks to <br/>
    formatted = formatted.replace(/\n/g, '<br/>');
    
    // Highlight code blocks with backticks
    formatted = formatted.replace(/`([^`]+)`/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in single quotes
    formatted = formatted.replace(/(?<=\s|^)'([^'\s][^']*?)'(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in double quotes
    formatted = formatted.replace(/(?<=\s|^)"([^"\s][^"]*?)"(?!\w)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Highlight values in parentheses
    formatted = formatted.replace(/(?<=\s|^)\(([^\s][^\)]*?)\)(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">($1)</span>');
    
    // Highlight values in pipes
    formatted = formatted.replace(/(?<=\s|^)\|([^\|\s][^\|]*?)\|(?=\s|[.,;:]|$)/g, '<span style="font-weight:bold;font-style:italic;background:#f3f4f6;padding:1px 4px;border-radius:4px;">$1</span>');
    
    // Render citation markers as Perplexity-style domain pills with hover popover.
    formatted = formatted.replace(/\[(\d{1,2})\]/g, (match, num) => {
      const idx = parseInt(num, 10) - 1;
      const src = sources && sources[idx];
      if (!src) {
        return '';
      }
      const name = (src.name || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const summary = (src.summary || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const category = (src.category || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const link = src.link || '';
      let host = '';
      try { host = link ? new URL(link).host.replace(/^www\./, '') : ''; } catch (_) {}
      const truncHost = host.length > 35 ? host.slice(0, 35) + '...' : host;
      const pillLabel = link || name;
      // Auth-gated proxy docs must open via Bearer-token fetch, not a raw navigation.
      const pillAnchorAttrs = isServerProxyDocUrl(link)
        ? `href="javascript:void(0);" class="citation-hover-wrap citation-link" data-doc-url="${link}"`
        : `href="${link || '#'}" target="_blank" rel="noopener noreferrer" class="citation-hover-wrap"`;
      return `<a ${pillAnchorAttrs} style="position:relative;display:inline-flex;align-items:center;padding:1px 7px;border-radius:4px;background:#f3f4f6;color:#6b7280;font-size:11px;font-weight:500;margin:0 2px;vertical-align:middle;cursor:pointer;white-space:nowrap;text-decoration:none;overflow:hidden;text-overflow:ellipsis;max-width:100%;display:inline-block;">${pillLabel}<span class="citation-popover" style="display:none;position:absolute;bottom:calc(100% + 8px);left:0;width:280px;background:white;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.12);padding:14px;z-index:50;text-align:left;cursor:default;white-space:normal;"><span style="display:block;font-size:11px;color:#6b7280;font-weight:600;margin-bottom:4px;">${category}</span><span style="display:block;font-size:13px;font-weight:700;color:#1e293b;margin-bottom:6px;line-height:1.3;">${name}</span><span style="display:block;font-size:12px;color:#64748b;line-height:1.4;margin-bottom:8px;">${summary.length > 120 ? summary.slice(0, 120) + '...' : summary}</span></span></a>`;
    });

    // Add search response prefix if this is a search response
    if (isSearchResponse) {
      formatted = `<div style="display: flex; align-items: center; margin-bottom: 8px; color: #2563eb; font-weight: 600; font-size: 12px;">
        <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 6px;">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        SEARCH RESULT
      </div>${formatted}`;
    }
    
    return formatted;
  };

  const renderSourceChunks = (sources) => {
    const cleanSummary = (raw) => {
      if (!raw) return '';
      if (raw.startsWith('{') || raw.startsWith('[')) {
        try {
          const parsed = JSON.parse(raw);
          return parsed.text || parsed.summary || parsed.content || '';
        } catch {
          return '';
        }
      }
      return raw;
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', color: '#2563eb', fontWeight: 600, fontSize: '12px' }}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: '6px' }}>
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          SEARCH RESULT
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {sources.map((source, i) => {
            const link = source.link || '#';
            const isAuthGated = isServerProxyDocUrl(link);
            const score = source.score != null
              ? (source.score <= 1 ? Math.round(source.score * 100) : Math.round(source.score))
              : null;
            const summary = cleanSummary(source.summary);

            const hasLink = !!link && link !== '#';
            const fileLabel = (() => {
              let fname = source.filename;
              if (!fname) {
                try { fname = decodeURIComponent(link.split('/').pop() || ''); }
                catch { fname = link.split('/').pop() || ''; }
                fname = fname.split(/[?#]/)[0];
              }
              const pageMatch = /[#&]page=(\d+)/i.exec(link);
              const page = source.pageNumber || (pageMatch ? pageMatch[1] : null);
              return page ? `${fname}#${page}` : fname;
            })();

            return (
              <div key={i}>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>
                  {hasLink ? (
                    <DocLink link={link} isAuthGated={isAuthGated} fileLabel={fileLabel} style={{ color: '#2563eb', textDecoration: 'none' }} hoverUnderline>
                      {source.name || 'Document'}
                    </DocLink>
                  ) : (
                    <span style={{ color: '#2563eb' }}>{source.name || 'Document'}</span>
                  )}
                  {score != null && <span style={{ color: '#374151' }}> | {score}% match</span>}
                </div>
                {summary && (
                  <p style={{ fontSize: '10px', color: '#374151', marginTop: '4px', marginBottom: 0, lineHeight: '1.6' }}>
                    <span dangerouslySetInnerHTML={{ __html: summary.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    {hasLink && (<>
                      {'  '}
                      <DocLink link={link} isAuthGated={isAuthGated} fileLabel={fileLabel} style={{ color: '#374151', textDecoration: 'underline', fontWeight: 500 }}>
                        Read more
                      </DocLink>
                    </>)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Pre-triage MCQ: user picked a product → re-run the search scoped to it.
  const selectSearchProduct = (query, product) => {
    if (!socket || !query) return;
    // Drop the choice prompt in place — no pick bubble; the placeholder takes
    // its spot and the server's scoped status ("...(ZPA)...") replaces it.
    setSearchMessages((prev) => prev.filter((m) => !m.isProductChoice));
    setSearchLoadingStatus("Searching knowledge articles...");
    socket.emit("searchProductSelected", { text: query, product });
  };

  // Send search message function - matching ChatWindow structure
  const sendSearchMessage = (overrideText = null, displayText = null) => {
    if (overrideText && typeof overrideText !== "string") {
      overrideText = null;
    }
    const queryText = (overrideText ?? message).trim();
    if (!queryText || !socket) return;
    const visibleText = displayText || queryText;
    const currentUser = localStorage.getItem("username") || "User";
    
    // Add message to search chat history
    const newMessage = { 
      text: visibleText, 
      sender: currentUser, 
      timestamp: Date.now(),
      isSearchQuery: true,
      sources: []
    };
    
    setSearchMessages(prev => [...prev, newMessage]);
    
    // Update title only on first message
    if (!isTitleSet) {
      const newTitle = getTitleFromMessage(visibleText);
      setSupportType(newTitle);
      setIsTitleSet(true);
    }
    
    // Emit search query - matching ChatWindow implementation
    console.log("🔍 Sending search query:", queryText);
    socket.emit("searchQuery", { text: queryText });
    setShowSearchChoice(false);
    
    // Clear message and reset UI
    setMessage("");
    setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = '28px';
      });
    }, 0);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const conversationKey = params.get("conversationKey");
    if (!conversationKey) return;

    const storedConversation = sessionStorage.getItem(conversationKey);
    if (storedConversation) {
      setConversationSearchContext(storedConversation);
    }
  }, [location.search]);

  // Handle Enter key for search - matching ChatWindow
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendSearchMessage();
    }
  };

  // Auto-resize textarea - matching ChatWindow
  const autoResizeTextarea = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  };

  // Auto-scroll to bottom when new messages arrive - matching ChatWindow implementation
  useEffect(() => {
    if (searchChatBodyRef.current) {
      searchChatBodyRef.current.scrollTop = searchChatBodyRef.current.scrollHeight;
    }
  }, [searchMessages]);

  // Set up scroll event listener and check scroll indicator - matching ChatWindow
  useEffect(() => {
    const container = searchChatBodyRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Check scroll indicator when search messages change - matching ChatWindow
  useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(checkScrollIndicator, 100);
  }, [searchMessages]);

  // Check if scroll indicator should be shown - matching ChatWindow
  const checkScrollIndicator = () => {
    if (!searchChatBodyRef.current) return;
    
    const container = searchChatBodyRef.current;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    
    // Show indicator if there's more content below the visible area
    const hasMoreContent = scrollHeight > clientHeight && scrollTop < (scrollHeight - clientHeight - 50);
    setShowScrollIndicator(hasMoreContent);
  };

  // Handle scroll events - matching ChatWindow
  const handleScroll = () => {
    checkScrollIndicator();
  };

  // Socket connection - matching ChatWindow
  useEffect(() => {
    const s = connectSocket();
    setSocket(s);

    s.on("connect", () => {
      console.log("✅ Socket connected:", s.id);
      const sessionId = s.id;
      const role = localStorage.getItem("role") || "user";
      s.emit("joinRoom", { sessionId, role, name: localStorage.getItem("username") || "User" });
    });

    s.on("searchStatus", (msg) => {
      setSearchLoadingStatus(msg.message);
    });

    // Pre-triage gate: server couldn't determine the product → ask the user.
    s.on("searchProductChoice", (msg) => {
      setSearchLoadingStatus(null);
      const { query, options, message } = msg || {};
      console.log("🔀 Product choice requested:", { query, options });
      setSearchMessages((prev) => [
        ...prev,
        {
          text: message || "Which product is this about?",
          sender: "bot",
          timestamp: Date.now(),
          isProductChoice: true,
          choiceQuery: query,
          choiceOptions: Array.isArray(options) ? options : [],
        },
      ]);
    });

    // Listen for search responses (RAG system responses) - matching ChatWindow implementation
    s.on("searchResponse", (msg) => {
      setSearchLoadingStatus(null);
      const { text, sender, timestamp, query, rag_sources, complete } = msg;
      console.log("🔍 Search response received:", { text, sender, timestamp, query });

      setSearchMessages((prev) => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;

        // Case 1: Update existing streaming message
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          const lastMsg = updated[lastIdx];

          // Use replace instead of += for controlled updates
          const newText = complete ? lastMsg.text : lastMsg.text + text;

          updated[lastIdx] = {
            ...lastMsg,
            text: newText,
            isStreaming: !complete,
            timestamp: complete ? timestamp : lastMsg.timestamp,
            sources: complete
              ? (rag_sources || []).map(normalizeSearchSource)
              : lastMsg.sources,
          };

          return updated;
        }

        // Case 2: No existing streaming message, so add new one
        return [
          ...prev,
          {
            text,
            sender,
            timestamp,
            isSearchResponse: true,
            isStreaming: !complete,
            sources: (rag_sources || []).map(normalizeSearchSource),
          },
        ];
      });
    });

    s.on("disconnect", () => {
      console.log("❌ Socket disconnected");
    });

    return () => {
      s.disconnect();
    };
  }, []);

  // Embedded auto-run: fire initialQuery once through the existing
  // sendSearchMessage flow, as soon as the socket is connected.
  useEffect(() => {
    if (!embedded || !initialQuery || !socket || autoRanRef.current) return;
    const q = String(initialQuery).trim();
    if (!q) return;
    autoRanRef.current = true;
    const run = () => sendSearchMessage(q);
    if (socket.connected) run();
    else socket.once("connect", run);
  }, [embedded, initialQuery, socket]);

  // Fact-aware research: when the user clicks the "research with more facts"
  // icon in the enterprise (/ai-agent) Search Mode header, re-run the search
  // with the diagnostic context gathered from the AI Mode (left) pane.
  const lastResearchNonceRef = useRef(0);
  useEffect(() => {
    if (!researchRequest || !researchRequest.nonce || !socket) return;
    if (researchRequest.nonce === lastResearchNonceRef.current) return;
    const q = String(researchRequest.text || "").trim();
    if (!q) return;
    lastResearchNonceRef.current = researchRequest.nonce;
    const run = () => sendSearchMessage(q, researchRequest.displayText);
    if (socket.connected) run();
    else socket.once("connect", run);
  }, [researchRequest, socket]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full w-full transition-colors duration-300 bg-[#F3F3ED]">
      {/* Add typewriter effect CSS - matching ChatWindow */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .typewriter-effect {
            animation: typing 2s steps(40, end);
          }

          @keyframes typing {
            from { width: 0 }
            to { width: 100% }
          }

          .citation-hover-wrap:hover .citation-popover {
            display: block !important;
          }
          .citation-hover-wrap::after {
            content: '';
            position: absolute;
            bottom: 100%;
            left: -4px;
            right: -4px;
            height: 14px;
          }
        `
      }} />
      
      {!standalone && (
        <SideBar
          mode="agent"
          onLogout={onLogout}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          onLinkClick={handleNavigation}
          currentPath={location.pathname}
          hideActions
        />
      )}
      
      {/* Chat Title - Move to top left, above the white background - matching ChatWindow */}
      {!embedded && (
      <div className={`fixed top-3 ${standalone ? "left-6" : "left-24"} z-[100]`}>
        <h1 className={`text-sm font-bold text-[#253439] tracking-wide uppercase ${isTyping ? 'after:content-["|"] after:ml-1 after:animate-pulse' : ''}`}
          style={{ letterSpacing: '0.05em' }}>
          {displayTitle}
        </h1>
      </div>
      )}

      {/* Move user count to the white background with higher z-index - matching ChatWindow */}
      {!embedded && (
      <div className="fixed top-3 right-8 z-[150] flex items-center gap-2">
        <UserProfileMenu onLogout={onLogout} />
      </div>
      )}

      {/* Main content area with Perplexity-like rounded border - matching ChatWindow */}
      <div className="flex-1 flex flex-col relative transition-all duration-300 ease-in-out p-1">
        {/* Rounded container with border - made much larger - matching ChatWindow */}
        <div className="flex-1 bg-white rounded-2xl border-2 border-[#F3F3ED] shadow-sm overflow-hidden relative">
          {/* === CHAT SCROLL AREA === */}
          <div className="flex flex-col flex-1 items-center w-full h-full">
            {/* Fade line at top for messages - matching ChatWindow */}
            <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-20 rounded-t-2xl"></div>



            {/* Main content area - matching ChatWindow */}
            <main
              ref={searchChatBodyRef}
              className="w-full max-w-4xl flex-1 overflow-y-auto flex flex-col px-6 py-6 hide-scrollbar"
              style={{
                minHeight: embedded ? "0" : "400px",
                maxHeight: embedded ? "none" : "calc(100vh - 140px)", // Even more available space
                background: "#FFFFFF",
                marginTop: "16px", // Consistent top margin for both modes
                marginBottom: "10px", // Much smaller bottom margin for more space
                position: "relative",
                zIndex: 10
              }}
              onClick={(e) => {
                const imageLink = e.target.closest('a.image-link');
                if (imageLink) {
                  e.preventDefault();
                  e.stopPropagation();
                  const imageUrl = imageLink.getAttribute('data-image-url');
                  if (imageUrl) {
                    handleImageClick(imageUrl);
                  }
                  return;
                }
                const docLink = e.target.closest('a.citation-link');
                if (docLink) {
                  e.preventDefault();
                  e.stopPropagation();
                  const docUrl = docLink.getAttribute('data-doc-url');
                  if (docUrl) {
                    openAuthenticatedFile(docUrl);
                  }
                }
              }}
            >
              <div className="w-full">
                {/* Search Mode - Perplexity-like UI with Multiple Queries - matching ChatWindow */}
                <div className="w-full space-y-8">
                  {/* Empty State for Search - matching ChatWindow */}
                  {searchMessages.length === 0 && showSearchChoice && (
                    <div className="flex flex-col items-center justify-center min-h-[24rem] text-gray-600 px-4">
                      <svg width="56" height="56" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4 text-gray-400">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                      <p className="text-lg font-semibold mb-2 text-[#253439]">Search</p>
                      <p className="text-sm text-center max-w-md mb-6">
                        Start a new search, or use the current interactive conversation as the search query.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                        <button
                          type="button"
                          onClick={() => setShowSearchChoice(false)}
                          className="text-left border border-gray-200 rounded-lg bg-white hover:border-[#06b6d4] hover:bg-cyan-50 px-4 py-4 transition"
                        >
                          <div className="font-semibold text-[#253439] mb-1">New Search</div>
                          <div className="text-sm text-gray-500">Type a fresh query and search the knowledge base.</div>
                        </button>
                        <button
                          type="button"
                          disabled={!conversationSearchContext}
                          onClick={() => sendSearchMessage(
                            conversationSearchContext,
                            "Search using existing conversation"
                          )}
                          className={`text-left border rounded-lg px-4 py-4 transition ${
                            conversationSearchContext
                              ? "border-gray-200 bg-white hover:border-[#06b6d4] hover:bg-cyan-50"
                              : "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          <div className="font-semibold mb-1">Existing conversation Search</div>
                          <div className="text-sm">
                            {conversationSearchContext
                              ? "Search using the conversation from the interactive window."
                              : "No interactive conversation was passed to this window."}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}

                  {searchMessages.length === 0 && !showSearchChoice && (
                    <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                      <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24" className="mb-4">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                      </svg>
                      <p className="text-lg font-medium mb-2">Search the knowledge base</p>
                      <p className="text-sm text-center max-w-md">
                        Ask any question and get answers from our comprehensive knowledge base with cited sources.
                      </p>
                    </div>
                  )}

                  {/* Process search messages to group queries with their responses - matching ChatWindow */}
                  {(() => {
                    const searchGroups = [];
                    let currentGroup = null;
                    
                    searchMessages.forEach((msg, idx) => {
                      if (msg.isSearchQuery) {
                        // Start new group with user query
                        if (currentGroup) {
                          searchGroups.push(currentGroup);
                        }
                        currentGroup = {
                          query: msg,
                          responses: [],
                          sources: []
                        };
                      } else if (currentGroup) {
                        // Add response to current group
                        currentGroup.responses.push(msg);
                        if (msg.sources?.length > 0) {
                          currentGroup.sources = msg.sources;
                        }
                      }
                    });
                    
                    // Add the last group if it exists
                    if (currentGroup) {
                      searchGroups.push(currentGroup);
                    }
                    
                    return searchGroups.map((group, groupIdx) => (
                      <div key={groupIdx} className="w-full border-b border-gray-100 pb-8 last:border-b-0">
                        {/* Search Query Display - user message bubble */}
                        <div className="mb-6 flex justify-start">
                          <div
                            className="rounded-2xl px-5 py-3 text-white max-w-[85%]"
                            style={{
                              background: '#06b6d4',
                              fontSize: '12px',
                              lineHeight: 'normal',
                              fontWeight: 'normal',
                            }}
                          >
                            {group.query.text}
                          </div>
                        </div>
                        <div>
                          {/* Hidden per UX request: Summary / Sources / Product Documents tabs. */}
                          {false && (
                          <div className="flex items-center mb-4 border-b border-gray-200">
                            {/* Answer Tab */}
                            <button
                              onClick={() => setSearchSubMode("rag")}
                              className={`px-4 py-2 text-sm font-medium transition ${
                                searchSubMode === "rag"
                                  ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                  : "text-gray-500 hover:text-[#253439]"
                              }`}
                            >
                              Summary
                            </button>
                            
                            {/* Sources Tab (All Sources) */}
                            <button
                              onClick={() => setSearchSubMode("sources")}
                              className={`px-4 py-2 text-sm font-medium transition ${
                                searchSubMode === "sources"
                                  ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                  : "text-gray-500 hover:text-[#253439]"
                              }`}
                            >
                              Sources ({group.sources?.length || 0})
                            </button>

                            {/* Dynamic Category Tabs - matching ChatWindow */}
                            {(() => {
                              if (!group.sources?.length) return null;
                              
                              // Group sources by category
                              const sourcesByCategory = group.sources.reduce((acc, source) => {
                                const category = source.category || "Product Documents";
                                if (!acc[category]) acc[category] = [];
                                acc[category].push(source);
                                return acc;
                              }, {});

                              const categories = Object.keys(sourcesByCategory);
                              
                              return categories.map((category) => (
                                <button
                                  key={category}
                                  onClick={() => setSearchSubMode(category)}
                                  className={`px-4 py-2 text-sm font-medium transition ${
                                    searchSubMode === category
                                      ? "border-b-2 border-[#06b6d4] text-[#06b6d4]"
                                      : "text-gray-500 hover:text-[#253439]"
                                  }`}
                                >
                                  {categoryDisplayNames[category] || category} ({sourcesByCategory[category].length})
                                </button>
                              ));
                            })()}
                          </div>
                          )}

                          {/* Hidden per UX request: runbook/source chips below the tabs. */}
                          {false && searchSubMode === "rag" && (
                            <div className="mb-6">
                              <div className="flex flex-wrap gap-3 mb-4">
                                {group.sources?.length > 0 &&
                                  group.sources.slice(0, 4)
                                    .map((source, i) => (
                                      <a
                                        key={i}
                                        href={source.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600 border hover:bg-gray-100 transition max-w-[160px]"
                                      >
                                        {source.icon === "pdf" ? (
                                          <div className={`${getCategoryColor(source.category || "Product Documents").iconColor} mr-2`}>
                                            <svg
                                              width="12"
                                              height="12"
                                              fill="currentColor"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              viewBox="0 0 24 24"
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                            </svg>
                                          </div>
                                        ) : (
                                          <div className={`w-4 h-4 ${getCategoryColor(source.category || "Product Documents").bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded mr-2`}></div>
                                        )}
                                        <span className="truncate">{source.name || "PDF"}</span>
                                      </a>
                                    ))}
                              </div>
                            </div>
                          )}

                          {/* Sources Content Display - matching ChatWindow */}
                          <div className="max-w-full overflow-hidden">
                            {(() => {
                              // Helper function to render sources - matching ChatWindow
                              const renderSources = (sourcesToRender, showCategoryBadge = true, showTopSourceSummary = false) => (
                                <div className="space-y-3 w-full max-w-full">
                                  {sourcesToRender.map((source, i) => {
                                    const categoryColors = getCategoryColor(source.category || "Product Documents");
                                    return (
                                    <div key={i} className="w-full max-w-full">
                                      <a
                                        href={source.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-full"
                                      >
                                        <div className={`w-8 h-8 ${categoryColors.bg} rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
                                          {source.icon === "pdf" ? (
                                            <svg
                                              width="14"
                                              height="14"
                                              fill="currentColor"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              viewBox="0 0 24 24"
                                              className={categoryColors.iconColor}
                                            >
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                              <polyline points="14,2 14,8 20,8" />
                                              <line x1="16" y1="13" x2="8" y2="13" />
                                              <line x1="16" y1="17" x2="8" y2="17" />
                                              <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                          ) : (
                                            <div className={`w-4 h-4 ${categoryColors.bg.replace('bg-', 'bg-').replace('-100', '-300')} rounded`}></div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                          <h3 className="text-sm font-medium text-gray-900 truncate">
                                            {source.name || "PDF"}
                                          </h3>
                                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 break-words">
                                            {source.summary || "No summary available"}
                                          </p>
                                          {showCategoryBadge && (
                                            <div className="mt-2">
                                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${categoryColors.bg} ${categoryColors.text}`}>
                                                {source.category || "Product Documents"}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </a>
                                      
                                      {/* AI-Generated Summary for Top Source in Category Tabs - matching ChatWindow */}
                                      {showTopSourceSummary && i === 0 && (() => {
                                        // Extract document filename from link
                                        const documentPath = source.link?.split('/').pop();
                                        const aiSummary = documentSummaries[documentPath];
                                        
                                        // Request summary if not already available
                                        if (!aiSummary && documentPath) {
                                          requestDocumentSummary(documentPath);
                                        }
                                        
                                        return (
                                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                                            <div className="flex items-start">
                                              <div className="flex-shrink-0">
                                                <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                              </div>
                                              <div className="ml-2 flex-1">
                                                <h4 className="text-sm font-medium text-blue-900">AI Summary</h4>
                                                <div className="mt-1 text-sm text-blue-800">
                                                  {aiSummary ? (
                                                    <p className="leading-relaxed">{aiSummary}</p>
                                                  ) : (
                                                    <div className="flex items-center space-x-2">
                                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                      <span className="text-xs text-blue-600">Generating summary...</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                })}
                              </div>
                            );

                              // Handle different tab modes - matching ChatWindow
                              if (searchSubMode === "sources") {
                                // Show all sources
                                return (
                                  <div className="mb-6">
                                    {group.sources?.length > 0 ? (
                                      renderSources(group.sources, true)
                                    ) : (
                                      <div className="text-center py-8 text-gray-500">
                                        <svg
                                          width="48"
                                          height="48"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1"
                                          viewBox="0 0 24 24"
                                          className="mx-auto mb-3"
                                        >
                                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                          <polyline points="14,2 14,8 20,8" />
                                        </svg>
                                        <p>No sources available yet</p>
                                        <p className="text-xs mt-1">Sources will appear after you search</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              } else if (searchSubMode !== "rag" && group.sources?.length > 0) {
                                // Show category-specific sources - matching ChatWindow
                                const categoryName = searchSubMode;
                                const categorySources = group.sources.filter(
                                  source => (source.category || "Product Documents") === categoryName
                                );

                                return (
                                  <div className="mb-6">
                                    {categorySources.length > 0 && (
                                      <div>
                                        {/* Top Source with AI Summary - matching ChatWindow */}
                                        <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100 shadow-sm max-w-[85%] self-start overflow-hidden">
                                          <div className="flex items-center mb-3">
                                            <h4 className="text-lg font-semibold text-gray-900 mr-2">Overview</h4>
                                            <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">Generated</div>
                                          </div>
                                          <div className="text-gray-700 leading-relaxed">
                                            {(() => {
                                              const firstSource = categorySources[0];
                                              const documentPath = firstSource.link?.split('/').pop();
                                              const aiSummary = documentSummaries[documentPath];
                                              if (!aiSummary && documentPath) requestDocumentSummary(documentPath);
                                              return aiSummary ? <div className="prose prose-sm max-w-none"><p>{aiSummary}</p></div> : (
                                                <div className="flex items-center space-x-3">
                                                  <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                                                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                  </div>
                                                  <span className="text-sm text-gray-600">Generating overview...</span>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                        {/* Top Source Card - matching ChatWindow */}
                                        <div className="w-full">
                                          <a
                                            href={categorySources[0].link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden mb-2"
                                          >
                                            <div className={`${getCategoryColor(categorySources[0].category || "Product Documents").iconColor} mr-3 mt-1 flex-shrink-0`}>
                                              <svg width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14,2 14,8 20,8" />
                                              </svg>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{categorySources[0].name || "PDF"}</h3>
                                              <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{categorySources[0].summary || "No summary available"}</p>
                                            </div>
                                          </a>
                                        </div>
                                        {/* Other Related Source Section - matching ChatWindow */}
                                        {categorySources.length > 1 && (
                                          <div className="mt-4">
                                            <div className="text-xs font-semibold text-gray-600 mb-2">Other Related Source</div>
                                            <div className="space-y-2">
                                              {categorySources.slice(1).map((source, i) => (
                                                <div key={i} className="w-full">
                                                  <a
                                                    href={source.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-start p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition w-full max-w-[85%] overflow-hidden"
                                                  >
                                                    <div className={`${getCategoryColor(source.category || "Product Documents").iconColor} mr-3 mt-1 flex-shrink-0`}>
                                                      <svg width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14,2 14,8 20,8" />
                                                      </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                      <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{source.name || "PDF"}</h3>
                                                      <p className="text-xs text-gray-500 leading-relaxed" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>{source.summary || "No summary available"}</p>
                                                    </div>
                                                  </a>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </div>
                        </div>

                        {/* Search Results Area - Only show in Answer tab - matching ChatWindow */}
                        {searchSubMode === "rag" && (
                          <div className="space-y-4">
                            {searchLoadingStatus && groupIdx === searchGroups.length - 1 && group.responses.length === 0 && (
                              <div className="w-full flex items-center gap-2 mb-2 px-1">
                                <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                                <span className="text-xs text-gray-400">{searchLoadingStatus}</span>
                              </div>
                            )}
                            {group.responses.map((msg, idx) => (
                              msg.isProductChoice ? (
                                <div key={idx} className="w-full flex flex-col mb-4 items-start">
                                  <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300">
                                    <div className="text-sm mb-3">{msg.text}</div>
                                    <div className="flex flex-wrap gap-2">
                                      {(msg.choiceOptions || []).map((opt) => (
                                        <button
                                          key={opt}
                                          onClick={() => selectSearchProduct(msg.choiceQuery, opt)}
                                          className="px-3 py-1.5 text-xs font-medium rounded-full border border-cyan-400 text-cyan-700 hover:bg-cyan-500 hover:text-white transition"
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div key={idx} className="w-full flex flex-col mb-4 items-start">
                                  <div className="px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm bg-white text-cyan-900 border-cyan-300" style={{ overflow: "visible" }}>
                                    {msg.sources?.length > 0
                                      ? renderSourceChunks(msg.sources)
                                      : <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text, msg.isSearchResponse, msg.sources) }} />
                                    }
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400 font-medium">
                                    {msg.sender === "bot" ? `${formatTimestamp(msg.timestamp || Date.now())}` : `${msg.sender || ""} ${formatTimestamp(msg.timestamp || Date.now())}`}
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* Fade gradient + fixed chat input — hidden when embedded (the
            enterprise split view drives search from its own top input). */}
        {!embedded && (
          <>
            {/* Fade gradient above chat input - matching ChatWindow */}
            <div
              className="fixed pointer-events-none"
              style={{
                left: standalone ? '50%' : 'calc(50% + 36px)',
                transform: 'translateX(-50%)',
                width: standalone ? '100%' : 'calc(100% - 72px)',
                maxWidth: '64rem',
                height: '40px',
                bottom: '120px',
                background: 'linear-gradient(to top, white 0%, white 40%, transparent 100%)',
                zIndex: 40
              }}
            />

            {/* Fixed Chat Input and Utility Bar - matching ChatWindow */}
            <div className={`fixed bottom-0 ${standalone ? 'left-0' : 'left-[72px]'} right-0 bg-white z-50`}>
              <div className="w-full max-w-4xl mx-auto px-6 pb-4">
                {/* Chat Input - matching ChatWindow */}
                <div className="bg-white rounded-xl border border-[#06b6d4] shadow-md flex items-start px-6 py-4">
                  <textarea
                    className="flex-1 bg-transparent border-none outline-none text-[#253439] placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto"
                    style={{
                      minHeight: '28px',
                      maxHeight: '200px',
                      height: '28px',
                      fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                      fontSize: "14px"
                    }}
                    placeholder="🔍 Search knowledge base..."
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      autoResizeTextarea(e.target);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendSearchMessage();
                      }
                    }}
                  />
                  {/* Send Button - matching ChatWindow */}
                  <button
                    className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition"
                    aria-label="Send"
                    onClick={sendSearchMessage}
                    type="button"
                  >
                    <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 2 11 13" />
                      <path d="M22 2l-7 20-4-9-9-4z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Image Zoom Modal */}
      {zoomImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center"
          onClick={closeZoom}
        >
          <div className="relative max-w-full max-h-full p-4">
            <img
              src={zoomImageUrl}
              alt="Zoomed"
              className="rounded-lg max-h-[90vh] max-w-[90vw] object-contain"
            />
            <button
              onClick={closeZoom}
              className="absolute top-2 right-2 text-white bg-black bg-opacity-60 hover:bg-opacity-90 p-2 rounded-full"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/>
                <path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TSEAgentRag;